import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import initialState from "./initialState";
import ActionTypes from "./ActionTypes";
import searchReducer from "./searchReducer";
import useDebouncedDispatch from "../../../_hook/useDebouncedDispatch";
import { isEqual } from "lodash";
import coursesData from "../../../_data/coursesData";
import WrapperProvider from "../../_base/WrapperProvider";


// Create the context for search state
export const searchContext = createContext();

// Custom hook to use search context
export function useSearch() {
  const value = useContext(searchContext);
  if (!value) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  if (!value && process.env.NODE_ENV !== "production") {
    throw new Error(
      "[search]: `useSearch` must be wrapped in a <SearchProvider />"
    );
  }
  return value;
}

export default function SearchProvider({
  children,
  storageKey = "searchState",
}) {
  if (!storageKey || typeof storageKey !== "string") {
    throw new Error("storageKey must be a non-empty string");
  }



  const [namespacedKey, initialValues] = useMemo(
    () => [`myApp_${storageKey}`, initialState],
    [storageKey, initialState]
  );

  // Initialize state with stored data from localStorage if available
  const [storedData, setStoredData] = useState(() => {
    try {
      if (typeof window === "undefined" || !window.localStorage) {
        console.warn("localStorage is not available");
        return initialValues;
      }

      const item = localStorage.getItem(namespacedKey);

      return item ? JSON.parse(item) : structuredClone(initialValues);
    } catch (error) {
      console.error(
        "Error accessing localStorage during initialization:",
        error
      );
      return structuredClone(initialValues);
    }
  });

  // Function to update localStorage with the new state data
  const setLocalStorageData = useCallback(
    (newObject) => {
      setStoredData((prev) => {
        try {
          const updatedObject =
            typeof newObject === "object" && newObject !== null
              ? { ...newObject }
              : {};

          if (!isSerializable(updatedObject)) {
            throw new Error("Data is not serializable");
          }

          if (JSON.stringify(prev) === JSON.stringify(updatedObject)) {
            return prev;
          }

          localStorage.setItem(namespacedKey, JSON.stringify(updatedObject));
          return updatedObject;
        } catch (error) {
          console.error("Error saving to localStorage:", error);
          return prev;
        }
      });
    },
    [namespacedKey, initialValues]
  );

  // Sync state with localStorage when it changes across tabs
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === namespacedKey) {
        try {
          const newValue = event.newValue
            ? JSON.parse(event.newValue)
            : structuredClone(initialValues);
          setStoredData(newValue);
        } catch (error) {
          console.error("Error parsing localStorage data from event:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [namespacedKey, initialValues]);

  // Middleware to apply additional logic after state updates
  const middlewareReducer = (state, action) => {
    try {
      const newState = searchReducer(state, action);

      if (!isEqual(storedData, newState)) {
        setLocalStorageData(newState);
      }

      return newState;
    } catch (error) {
      console.error("Error in middlewareReducer:", error);
      return state;
    }
  };

  const [state, dispatch] = useReducer(
    middlewareReducer,
    { ...initialValues, ...storedData },
    (stored) => stored ?? initialValues
  );
  // Generic dispatch handler function
  const handleDispatch = useDebouncedDispatch(dispatch, 100);

  // Action dispatchers for loading, error, and data updates
  const setLoading = (isLoading) =>
    dispatch({ type: ActionTypes.SET_LOADING, payload: isLoading });

  const setError = (error) =>
    dispatch({ type: ActionTypes.SET_ERROR, payload: error });

  const handleData = (type, payload) => {
    try {
      setLoading(true);
      setError(null);
      dispatch({ type, payload });
    } catch (error) {
      setError(`Error in handleData: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle global errors
  // useEffect(() => {
  //   const handleGlobalError = (error) => {
  //     console.error("Unhandled error in SharedProvider:", error);
  //     setError(`An unexpected error occurred: ${JSON.stringify(error)}`);
  //   };

  //   window.addEventListener("error", handleGlobalError);
  //   window.addEventListener("unhandledrejection", (event) =>
  //     handleGlobalError(event.reason)
  //   );

  //   return () => {
  //     window.removeEventListener("error", handleGlobalError);
  //     window.removeEventListener("unhandledrejection", handleGlobalError);
    
  //   };
  // }, []);

  useEffect(() => {
    handleData(ActionTypes.SEARCHING_RESULTS, { results: coursesData });
  }, [state?.searchQuery]);



  // Functions for specific actions

  const searchActions = {
    setFilterQuery: (payload) => {
      handleDispatch(ActionTypes.SET_FILTERS_QUERY, payload);
    },
    clearFilters: (payload) => {
      handleDispatch(ActionTypes.CLEAR_FILTERS, payload);
    },
    setSortingParameters: (payload) => {
      handleDispatch(ActionTypes.SET_SORTING_PARAMETERS, payload);
    },
  };
  const stateUpdate = useMemo(
    () => ({
      ...state,
      ...storedData,
    }),
    [storedData]
  );
  const value = useMemo(
    () => ({
      ...stateUpdate,
      ...searchActions,
      dispatch,
    }),
    [stateUpdate]
  );

  return (
    <searchContext.Provider value={value}>
      <WrapperProvider
        stateUpdate={stateUpdate}
        setLoading={setLoading}
        setError={setError}
      >
        {children}
      </WrapperProvider>
    </searchContext.Provider>
  );
}

const isSerializable = (data) => {
  try {
    JSON.stringify(data);
    return true;
  } catch {
    return false;
  }
};
